import {
	Checkbox,
	DeprecatedCreativeModal as CreativeModal,
	DeprecatedScrollbar as Scrollbar,
	FlexColumn,
	FlexRow,
	Label,
	SubHeading,
	Text,
} from '@forecast-it/design-system';
import React, {useEffect, useState} from 'react';
import Util from '../../../forecast-app/shared/util/util';
import styled from 'styled-components/macro';
import {useIntl} from 'react-intl';
import {CheckedState} from '@radix-ui/react-checkbox';
import DirectApi from '../../../directApi';
import InlineLoader from '../../../forecast-app/shared/components/inline-loader/inline_loader';
import ImportSageIntacctDepartmentsMutation from '../../../mutations/ImportSageIntacctDepartmentsMutation';
import {hideLoader, showLoader} from '../../global_loader';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {HorizontalBar} from './SageIntacctModal.styled';

type ListItemType = {
	hasBorderBottom?: boolean;
};

const scrollSectionHeight = window?.innerHeight < 800 ? '200px' : '500px';
const ListItem = styled.div<ListItemType>`
	width: 550px;
	padding: 18px 8px 18px 8px;
	border-bottom: ${({hasBorderBottom}) => (hasBorderBottom ? '1px solid #ccc' : 'none')};
`;

const ListWrapper = styled.div`
	height: ${scrollSectionHeight};
`;

type SageIntacctImportDepartmentsModalProps = {
	closeModal: () => void;
	companyId: string;
};

type SageIntacctDepartment = {
	DEPARTMENTID: number;
	NAME: string;
};

const SageIntacctImportDepartmentsModal = ({closeModal, companyId}: SageIntacctImportDepartmentsModalProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [departments, setDepartments] = useState<SageIntacctDepartment[]>([]);
	const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
	const [selectAll, setSelectAll] = useState<CheckedState>(false);
	const [fetchingDepartments, setFetchingDepartments] = useState(true);
	const [failed, setFailed] = useState(false);
	const [importing, setImporting] = useState(false);
	const disableImportButton = importing || selectedDepartments.length === 0;

	useEffect(() => {
		DirectApi.Fetch('sage_intacct/departments').then(res => {
			if (res?.departments) {
				setDepartments(res?.departments);
				setFetchingDepartments(false);
			} else if (res?.error || res?.status === 500 || res?.statusCode === 500) {
				setFailed(true);
				setFetchingDepartments(false);
			} else {
				setDepartments([]);
				setFetchingDepartments(false);
			}
		});
	}, []);

	const selectAllClick = () => {
		if (selectAll) {
			setSelectedDepartments([]);
			setSelectAll(false);
		} else {
			const selectedDeps = departments.map(d => d.DEPARTMENTID);
			setSelectedDepartments(selectedDeps);
			setSelectAll(true);
		}
	};

	const departmentClick = (inputId: number) => {
		setSelectAll('indeterminate');
		if (selectedDepartments.includes(inputId)) {
			const selectedDeps = selectedDepartments.filter(id => id !== inputId);
			if (selectedDeps.length === 0) {
				setSelectAll(false);
			}
			setSelectedDepartments(selectedDeps);
		} else {
			const selectedDeps = [...selectedDepartments];
			selectedDeps.push(inputId);
			if (selectedDeps.length === departments.length) {
				setSelectAll(true);
			}
			setSelectedDepartments(selectedDeps);
		}
	};

	const onImportClick = () => {
		setImporting(true);
		showLoader();
		Util.CommitMutation(
			ImportSageIntacctDepartmentsMutation,
			{
				sageIntacctDepartmentIds: selectedDepartments,
				companyId: companyId,
			},
			() => {
				setImporting(false);
				closeModal();
				hideLoader();
				createToast({
					duration: 5000,
					message: `${selectedDepartments.length} Departments imported from Sage Intacct`,
				});
			}
		);
	};

	return (
		<CreativeModal
			title={'Import Departments from Sage Intacct'}
			size={'l'}
			closeModal={closeModal}
			creativeButtonText={`Import Selected Departments (${selectedDepartments.length})`}
			cancelButtonText={formatMessage({id: 'common.cancel'})}
			onCreateHandler={onImportClick}
			onCancelHandler={closeModal}
			disabled={disableImportButton}
			replicateDesignSystem
		>
			<FlexColumn gap={'xs'}>
				<Label>Departments</Label>
				<Text color={Text.color.medium}>Please select the departments you wish to import from Sage Intacct</Text>
			</FlexColumn>
			{failed && <Text color={'error'}>Something has gone wrong, try again or contact support.</Text>}
			<ListItem>
				<FlexRow gap={'xl'}>
					<Checkbox onChange={selectAllClick} checked={selectAll} />
					<SubHeading size={'2'}>{'Select all'}</SubHeading>
				</FlexRow>
			</ListItem>
			<ListWrapper>
				{fetchingDepartments ? (
					<InlineLoader />
				) : (
					<Scrollbar maxHeight={scrollSectionHeight}>
						<FlexColumn alignItems="flex-start" justifyContent="flex-start">
							{departments &&
								departments.map((dep: SageIntacctDepartment) => {
									return (
										<ListItem key={'department-' + dep.DEPARTMENTID} hasBorderBottom>
											<FlexRow gap={'xl'}>
												<Checkbox
													onChange={() => departmentClick(dep.DEPARTMENTID)}
													checked={selectedDepartments.includes(dep.DEPARTMENTID)}
												/>
												<Text size={'2'}>{dep.NAME}</Text>
											</FlexRow>
										</ListItem>
									);
								})}
						</FlexColumn>
					</Scrollbar>
				)}
			</ListWrapper>
			<HorizontalBar />
		</CreativeModal>
	);
};

export default SageIntacctImportDepartmentsModal;
